import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { add, diff } from 'next/lib/date';
import { selectAccount, Shape as MetaShape } from 'next/entities/account';
import { readOverview, Shape as OverviewShape } from 'next/entities/overview';
import { asRequest } from 'next/entities/requests';
import { selectUser, Shape as UserShape } from 'next/entities/user';
import { Link, Progress, ErrorProgress, Skeleton } from 'next/components/ui';
import { Counter, TrialMessage, TrialWrapper } from './styled';

const FLOWS_THRESHOLD = 10;
const SIGNUP_SUBJECT = 'Sign Up for Appcues';
const SIGNUP_MESSAGE = [
  'Hi,',
  "I'd like to sign up for an Appcues subscription.",
  'Thanks!',
].join('\n\n');

export const TrialMeter = ({ account, className, overview = {}, user }) => {
  const {
    createdAt,
    flowsShownLimit: limit = 0,
    isTrial,
    isTrialExpired: expired = false,
    trialLengthDays: days = 0,
  } = account ?? {};

  const { email, fullname: name } = user ?? {};

  const handleUpgrade = useCallback(() => {
    try {
      /* eslint-disable new-cap */
      window.Beacon('prefill', {
        name,
        email,
        subject: SIGNUP_SUBJECT,
        text: SIGNUP_MESSAGE,
      });
      window.Beacon('navigate', '/ask/');
      window.Beacon('open');
      /* eslint-enable new-cap */
    } catch {
      // If beacon fail we don't care since we are going to
      // subscription page anyway
    }
  }, [name, email]);

  // Render nothing if not in trial
  if (!isTrial) {
    return null;
  }

  // Display a loader if still fetching overview analytics
  if (overview.loading) {
    return (
      <TrialWrapper className={className}>
        <Skeleton data-testid="loader" />
      </TrialWrapper>
    );
  }

  const shown = overview.data.flows_shown || 0;

  const active = limit - shown > 0;
  const remaining = Math.ceil(
    diff(Date.now(), add(createdAt, days, 'days'), 'days')
  );
  const hasEnded = expired || (!active && remaining <= 0);

  if (hasEnded) {
    return (
      <TrialWrapper className={className}>
        <TrialMessage>You&apos;ve reached your trial limit</TrialMessage>
        <Link to="/subscription" onClick={handleUpgrade}>
          Upgrade now
        </Link>
      </TrialWrapper>
    );
  }

  const progress = active ? shown / limit : (days - remaining) / days;
  const percentage = Math.round(progress * 100, 2);

  return (
    <TrialWrapper className={className}>
      {active && (
        <Counter>
          {shown} of {limit}
        </Counter>
      )}

      <TrialMessage>
        {active
          ? 'Flows shown in trial'
          : `Trial expires in ${remaining} day${remaining === 1 ? '' : 's'}`}
      </TrialMessage>

      {!active || limit - shown > FLOWS_THRESHOLD ? (
        <Progress value={percentage} />
      ) : (
        <ErrorProgress value={percentage} />
      )}

      <Link to="/subscription" onClick={handleUpgrade}>
        Upgrade now
      </Link>
    </TrialWrapper>
  );
};

TrialMeter.propTypes = {
  account: MetaShape,
  className: PropTypes.string,
  overview: asRequest(OverviewShape),
  user: UserShape,
};

const mapStateToProps = state => ({
  account: selectAccount(state),
  overview: readOverview(state),
  user: selectUser(state),
});

export default connect(mapStateToProps)(TrialMeter);
